import { useCallback, useEffect, useState } from 'react';
import { Monitor, Save } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { adminUpdate } from '../../lib/adminApi';

interface CustomSiteKoppelingSectionProps {
  userId: string;
}

/**
 * Admin-blok voor de maatwerk "Eigen website"-koppeling. De admin richt de
 * koppeling zelf in (buiten dibbi om) en zet hem hier op actief; de gebruiker
 * ziet dat terug als 'Actief' op de Eigen website-tegel in Instellingen > Koppeling.
 *
 * Laadt zijn eigen data rechtstreeks uit `users` (RLS: admins lezen alle rijen),
 * zodat de admin-users edge function hier niet voor aangepast hoeft te worden.
 */
export function CustomSiteKoppelingSection({ userId }: CustomSiteKoppelingSectionProps) {
  const [siteUrl, setSiteUrl] = useState<string | null>(null);
  const [active, setActive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editUrl, setEditUrl] = useState('');
  const [editActive, setEditActive] = useState(false);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from('users')
      .select('custom_site_url, custom_site_active')
      .eq('id', userId)
      .maybeSingle();
    setSiteUrl(data?.custom_site_url ?? null);
    setActive(!!data?.custom_site_active);
    setEditUrl(data?.custom_site_url ?? '');
    setEditActive(!!data?.custom_site_active);
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  const dirty = editActive !== active || (editUrl.trim() || null) !== (siteUrl || null);

  const handleSave = async () => {
    setSaving(true);
    setSuccess('');
    setError('');
    try {
      await adminUpdate({
        table: 'users',
        operation: 'update',
        id: userId,
        data: {
          custom_site_url: editUrl.trim() || null,
          custom_site_active: editActive,
        },
      });
      await load();
      setSuccess('Eigen website-koppeling opgeslagen');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error saving custom site koppeling:', err);
      setError('Opslaan mislukt, probeer het opnieuw');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6">
      <div className="flex items-center gap-2 mb-1">
        <Monitor className="w-5 h-5 text-[#FF6B35]" />
        <h3 className="text-lg font-semibold text-gray-900">Eigen website-koppeling</h3>
        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${loading ? 'bg-gray-300 animate-pulse' : active ? 'bg-emerald-500' : 'bg-gray-400'}`} />
          {loading ? 'Laden...' : active ? 'Actief' : 'Niet actief'}
        </span>
      </div>
      <p className="text-sm text-gray-600 mb-4">
        Maatwerk-koppeling voor niet-WordPress-websites. Zet op actief zodra de koppeling voor
        deze gebruiker is ingericht; de gebruiker ziet de status op de Eigen website-tegel bij
        Instellingen, Koppeling.
      </p>

      <div className="flex flex-col sm:flex-row sm:items-end gap-4">
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-1">Website-URL</label>
          <input
            type="text"
            value={editUrl}
            onChange={(e) => setEditUrl(e.target.value)}
            placeholder="https://www.voorbeeld.nl"
            disabled={loading}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#FF6B35] disabled:bg-gray-50"
          />
        </div>
        <label className="flex items-center gap-2 cursor-pointer select-none pb-2">
          <input
            type="checkbox"
            checked={editActive}
            onChange={(e) => setEditActive(e.target.checked)}
            disabled={loading}
            className="w-4 h-4 text-[#FF6B35] border-gray-300 rounded focus:ring-[#FF6B35]"
          />
          <span className="text-sm font-medium text-gray-700">Koppeling actief</span>
        </label>
        <button
          onClick={handleSave}
          disabled={saving || loading || !dirty}
          className="inline-flex items-center gap-2 bg-[#FF6B35] hover:bg-[#e85a20] disabled:opacity-50 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
        >
          <Save className="w-4 h-4" />
          {saving ? 'Opslaan...' : 'Opslaan'}
        </button>
      </div>

      {success && <p className="text-sm text-emerald-600 mt-3">{success}</p>}
      {error && <p className="text-sm text-red-600 mt-3">{error}</p>}
    </div>
  );
}
